import {
  createListenerMiddleware,
  createSlice,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import { equipesAPI } from "@/services/equipe.service";
import { localAPI } from "@/services/local.service";
import { readAccessTokenForRequest } from "@/lib/auth-session";
import { SetLocais } from "./login.slice";
import type { RootState } from "../store";
import { IUsuario } from "@/interfaces/usuarios.interface";
import {
  authSlice,
  resetState,
  clearAuthStateOnly,
  setAuthenticatedUser,
  syncAuthenticatedUserFromPeer,
} from "./auth.slice";
import { SYSIGREJA_INTERNO_LOGIN_ID } from "@/constants/sysigreja-interno-login-id";
import { arrayUniqueByKey } from "@/lib/utils";
import { baseApi } from "@/services/base.service";
import { authAPI } from "@/services/auth.service";
import { whatsappAPI } from "@/services/whatsapp.service";
import { modesAPI } from "@/services/modes.service";
import { pedidosOracaoPublicAPI } from "@/services/pedidos-oracao-public.service";
import { conquistasApi, type ListarConquistasResponse, type GetConquistasArgs } from "@/services/conquistas.service";
import { loginAPI } from "@/services/login.service";

export interface GeneralState {
  showLoader: boolean;
  showFeatureFlags: boolean;
  navOpened: boolean;
  StepsTour: any[];
  LoginId: number | null;
  ocultarEtiquetasImpressao: boolean;
}

function loadInitialLoginId(): number | null {
  if (typeof window === "undefined") return null;
  const onTrialRoute =
    window.location.pathname === "/trial" ||
    window.location.pathname.startsWith("/trial/");
  const fromUrl = Number(new URL(location.href).searchParams.get("loginId"));
  // loginId=12 em deep links de devocionais não deve virar org ativa da sessão
  // (exceto no funil /trial, onde o tenant interno é a org correta).
  if (
    fromUrl > 0 &&
    !Number.isNaN(fromUrl) &&
    (onTrialRoute || fromUrl !== SYSIGREJA_INTERNO_LOGIN_ID)
  ) {
    return fromUrl;
  }
  const fromStorage = Number(localStorage.getItem("LoginId"));
  if (
    fromStorage > 0 &&
    !Number.isNaN(fromStorage) &&
    (onTrialRoute || fromStorage !== SYSIGREJA_INTERNO_LOGIN_ID)
  ) {
    return fromStorage;
  }
  if (onTrialRoute) return SYSIGREJA_INTERNO_LOGIN_ID;
  return null;
}

/** Resolve LoginId a partir do usuário autenticado (login, URL ou storage). */
export function resolveLoginIdFromUser(user: IUsuario): number | null {
  if (typeof window === "undefined") return null;

  const onTrialRoute =
    window.location.pathname === "/trial" ||
    window.location.pathname.startsWith("/trial/");
  const loginIdPath = Number(new URL(location.href).searchParams.get("loginId"));
  const loginIdStorage = Number(localStorage.getItem("LoginId"));
  const logins = user.logins ?? [];
  const isMaster = user.Master === true;
  const allowInterno = isMaster || onTrialRoute;

  // Deep links de devocionais usam loginId=12 (SysIgreja); não promover a org ativa
  // para não-master — o módulo usa o tenant da URL só nas APIs.
  // Em /trial o tenant interno é exatamente a org da sessão.
  const pathUsavel =
    loginIdPath > 0 &&
    !Number.isNaN(loginIdPath) &&
    logins.some((x) => x.Id === loginIdPath) &&
    (allowInterno || loginIdPath !== SYSIGREJA_INTERNO_LOGIN_ID);

  if (pathUsavel) {
    return loginIdPath;
  }

  const storageUsavel =
    loginIdStorage > 0 &&
    !Number.isNaN(loginIdStorage) &&
    logins.some((x) => x.Id === loginIdStorage) &&
    (allowInterno || loginIdStorage !== SYSIGREJA_INTERNO_LOGIN_ID);

  if (storageUsavel) {
    return loginIdStorage;
  }

  if (onTrialRoute && logins.some((l) => l.Id === SYSIGREJA_INTERNO_LOGIN_ID)) {
    return SYSIGREJA_INTERNO_LOGIN_ID;
  }

  const fromLogins = logins.find(
    (l) => allowInterno || l.Id !== SYSIGREJA_INTERNO_LOGIN_ID,
  )?.Id;
  if (fromLogins != null && fromLogins > 0) return fromLogins;

  const fromEventos = (
    arrayUniqueByKey(
      (user.usuarioEventos ?? []).map((uv) => uv.eventosview.logins),
      "Id",
    ) as { Id?: number }[]
  )?.[0]?.Id;

  return fromEventos != null && fromEventos > 0 ? fromEventos : null;
}

function applyResolvedLoginId(state: GeneralState, loginId: number | null) {
  if (!loginId || loginId <= 0) return;
  state.LoginId = loginId;
  localStorage.setItem("LoginId", loginId.toString());
}

const initialState: GeneralState = {
  showLoader: false,
  showFeatureFlags: false,
  navOpened: false,
  StepsTour: [],
  ocultarEtiquetasImpressao: new URL(location.href).searchParams.get("ocultarEtiquetasImpressao") === "true",
  LoginId: loadInitialLoginId(),
};

// Função para resetar todo o cache de queries
const resetAllQueriesCache = (dispatch: any) => {
  console.log("[CACHE_RESET] Iniciando reset de todo o cache de queries");

  // Reset do cache de todas as APIs
  dispatch(baseApi.util.resetApiState());
  dispatch(authAPI.util.resetApiState());
  dispatch(whatsappAPI.util.resetApiState());
  dispatch(modesAPI.util.resetApiState());
  dispatch(pedidosOracaoPublicAPI.util.resetApiState());
  dispatch(equipesAPI.util.resetApiState());

  console.log("[CACHE_RESET] Cache de todas as queries resetado com sucesso");
};

export const generalSlice = createSlice({
  name: "generalSlice",
  initialState,
  reducers: {
    setShowLoader(state: GeneralState, { payload }: PayloadAction<boolean>) {
      state.showLoader = payload;
    },
    setShowFeatureFlags(
      state: GeneralState,
      { payload }: PayloadAction<boolean>
    ) {
      state.showFeatureFlags = payload;
    },
    setNavOpened(state: GeneralState, { payload }: PayloadAction<boolean>) {
      state.navOpened = payload;
    },
    setOcultarEtiquetasImpressao(state: GeneralState, { payload }: PayloadAction<boolean>) {
      state.ocultarEtiquetasImpressao = payload;
    },
    setLoginId(state: GeneralState, { payload }: PayloadAction<number | null | undefined>) {
      if (payload == null || payload === undefined) {
        return;
      }

      const loginIdNumber = Number(payload);
      if (Number.isNaN(loginIdNumber) || loginIdNumber <= 0) {
        return;
      }

      if (state.LoginId === loginIdNumber) {
        return;
      }

      state.LoginId = loginIdNumber;
      localStorage.setItem("LoginId", loginIdNumber.toString());
    },
  },
  extraReducers: (builder) => {
    const handleAuthCleared = (state: GeneralState) => {
      const pathname = window.location.pathname;
      const isPublicPage = pathname.includes('/cursos') ||
        pathname.includes('/inscricao') ||
        pathname.includes('/inscricoes') ||
        pathname.includes('/eventos') ||
        (!pathname.startsWith('/sistema') && !pathname.startsWith('/login'));

      if (isPublicPage) {
        const loginIdToPreserve = state.LoginId || Number(localStorage.getItem("LoginId")) || null;
        if (loginIdToPreserve) {
          state.LoginId = loginIdToPreserve;
          localStorage.setItem("LoginId", loginIdToPreserve.toString());
          return;
        }
      }

      state.LoginId = null;
      localStorage.setItem("LoginId", "");
    };

    builder.addCase(resetState, (state) => {
      handleAuthCleared(state);
    });
    builder.addCase(clearAuthStateOnly, (state) => {
      handleAuthCleared(state);
    });
    builder.addCase(setAuthenticatedUser, (state, { payload }) => {
      applyResolvedLoginId(state, resolveLoginIdFromUser(payload));
    });
    builder.addCase(syncAuthenticatedUserFromPeer, (state, { payload }) => {
      applyResolvedLoginId(state, resolveLoginIdFromUser(payload));
    });
  },
});

export const {
  setShowLoader,
  setNavOpened,
  setLoginId,
  setShowFeatureFlags,
  setOcultarEtiquetasImpressao,
} = generalSlice.actions;
export const generalReducer = generalSlice.reducer;

// Seletor base
const selectGeneralState = (state: RootState) => state.generalReducer;

// Seletores básicos (mantido por compatibilidade)
export const selectGeneral = selectGeneralState;

// Novos seletores memoizados para otimização
export const generalSelectors = {
  selectLoginId: createSelector(
    [selectGeneralState],
    (general) => general.LoginId
  ),

  selectShowLoader: createSelector(
    [selectGeneralState],
    (general) => general.showLoader
  ),

  selectNavOpened: createSelector(
    [selectGeneralState],
    (general) => general.navOpened
  ),

  selectShowFeatureFlags: createSelector(
    [selectGeneralState],
    (general) => general.showFeatureFlags
  ),

  selectOcultarEtiquetasImpressao: createSelector(
    [selectGeneralState],
    (general) => general.ocultarEtiquetasImpressao ?? false
  ),

  // Seletor combinado para estados de UI
  selectUIState: createSelector(
    [selectGeneralState],
    (general) => ({
      showLoader: general.showLoader,
      navOpened: general.navOpened,
      showFeatureFlags: general.showFeatureFlags,
    })
  ),
};

export const generalListenerMiddleware = createListenerMiddleware();
generalListenerMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    if (
      action.type === authSlice.actions.setAuthenticatedUser.type ||
      action.type === authSlice.actions.syncAuthenticatedUserFromPeer.type
    ) {
      return true;
    }

    if (action.type === setLoginId.type) {
      return true;
    }
  },
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const previousState = listenerApi.getOriginalState() as RootState;

    // Se o LoginId mudou, resetar todo o cache
    if (action.type === setLoginId.type) {
      const currentLoginId = state.generalReducer.LoginId;
      const previousLoginId = previousState.generalReducer.LoginId;

      if (currentLoginId !== previousLoginId) {
        console.log(
          "[CACHE_RESET] LoginId mudou de",
          previousLoginId,
          "para",
          currentLoginId
        );
        // Não resetar cache se o LoginId está sendo setado como null
        if (currentLoginId != null) {
          const conquistasSnapshot = conquistasApi.endpoints.getConquistas.select({
            plataforma: "web",
          })(previousState);
          resetAllQueriesCache(listenerApi.dispatch);
          if (conquistasSnapshot?.data) {
            listenerApi.dispatch(
              (
                conquistasApi.util.upsertQueryData as (
                  endpointName: "getConquistas",
                  args: GetConquistasArgs,
                  data: ListarConquistasResponse,
                ) => ReturnType<typeof conquistasApi.util.upsertQueryData>
              )("getConquistas", { plataforma: "web" }, conquistasSnapshot.data),
            );
          }
          // Limpar locais antigos e recarregar para o novo login
          listenerApi.dispatch(SetLocais(null));
          // Página pública (inscrição) seta LoginId pelo slug sem sessão: /locais/byLogin exige login (401).
          if (readAccessTokenForRequest(state.authReducer?.access_token)) {
            listenerApi.dispatch(
              localAPI.endpoints.getLocais.initiate(null, { forceRefetch: true })
            );
          }
          if (
            typeof window !== "undefined" &&
            window.location.pathname.includes("/sistema/backoffice")
          ) {
            listenerApi.dispatch(loginAPI.util.invalidateTags(["IgrejasParceiras"]));
          }
        } else {
          console.warn("[INSCRICAO_DEBUG] LoginId mudou para null, NÃO resetando cache para evitar loop");
        }
      }
    }

    listenerApi.cancelActiveListeners();
    if (state.generalReducer.LoginId && state.authReducer.email) {
      listenerApi.dispatch(
        equipesAPI.endpoints.getEquipanteLogin.initiate(
          {
            email: state.authReducer.email,
            loginId: state.generalReducer.LoginId,
          },
          {
            forceRefetch: true,
          }
        )
      );
    }
  },
});
